import { useState, useCallback, useEffect } from 'react'
const { useWindowDimensions } = XPReactNative
import { useUser } from '@clerk/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TZAApiClient, useAuthedRequestFn, color, TZABrandGuidelines, useNavigate, useResizeObserver, getImageColorStyle } from '@my/util'
import { APP_ENV } from '@my/config'
import {
  YStack,
  SimpleText,
  ScrollView,
  Image,
  FlatListGridView,
  ActionButton,
  XStack,
  Spinner,
  XPReactNative, 
  DataLoadingDialog,
  ActiveButton
} from '@my/ui'

import assets from '../../assets/images'

import { ModalDialog } from './modal/ChatModal'
import { KeyInsights } from './keyInsights/KeyInsights'
import { HomeMainHeading } from '../../components/index'

import { dataHome, FinancialProfileButton } from './helper'
import { QuestionOfDaySection } from './questionoftheDay'
import { WelcomeSection } from './welcome'
import { Platform } from 'react-native'

// Custom Hooks
function useQuestionOfDay({
  authedRequestFn,
  queryClient,
  selectedAnswer,
  setSelectedAnswer,
  setIsAnswerRevealed,
  isAnswerRevealed,
}) {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const date = new Date().toISOString().split('T')[0]
  const queryKey = [date, zone]

  const { data: questionOfDay } = useQuery({
    queryKey,
    queryFn: () => authedRequestFn(`/user/qotd?timestamp=${new Date().getTime()}&zone=${zone}`),
  })

  const mutation = useMutation({
    mutationFn: (formData: any) =>
      authedRequestFn('/user/qotd', {
        method: 'POST',
        body: formData,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const handleAnswerPress = (answer: string) => {
    if (!questionOfDay) return
    const payload = {
      questionIndex: questionOfDay.questionIndex,
      selectedAnswer: answer,
    }
    mutation.mutate(payload)
    setSelectedAnswer(answer)
    setIsAnswerRevealed(true)
  }

  useEffect(() => {
    if (questionOfDay?.selectedAnswer) {
      setSelectedAnswer(questionOfDay.answers[questionOfDay.selectedAnswer])
      setIsAnswerRevealed(true)
    }
  }, [questionOfDay])

  return {
    questionOfDay,
    selectedAnswer,
    isAnswerRevealed,
    handleAnswerPress,
  }
}

function useReviewData(authedRequestFn) {
  const { data: reviewData, isLoading } = useQuery({
    queryKey: ['financial-review'],
    queryFn: () => authedRequestFn('/financial-review'),
  })

  return { reviewData, isLoading }
}

const FORCE_REGISTRATION = false;

export function HomeScreen() {
  const { user } = useUser()
  const { width } = useWindowDimensions()
  const [dimensions, dimensionProps] = useResizeObserver();
  const authedRequestFn = useAuthedRequestFn()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const userDataQuery = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => authedRequestFn('/user-profile'),
  }) //'Accepted Legal Terms'

  useEffect(() => {
    if (!userDataQuery.isLoading) {
      const notAcceptedTerms = !userDataQuery.data?.['Accepted Legal Terms'] || !userDataQuery.data?.['Accepted Investment Advisory Agreement'] || !userDataQuery.data?.['subscription']?.['productName']
      if (FORCE_REGISTRATION || notAcceptedTerms) {
        navigate('/registration', { replace: true });
      } else if (!notAcceptedTerms) {
        if (isMobile && APP_ENV.TZA_ENV === 'production') {
          //navigate('/not-supported-device');
        }
      }

    }
  }, [userDataQuery.isLoading, userDataQuery.data])

  const { reviewData, isLoading } = useReviewData(authedRequestFn)
  const financialProfileQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  });
  const dataIsLoading = financialProfileQuery.isLoading || isLoading;
  const financialProfileCompleted = (Object.keys(financialProfileQuery?.data || {}).length > 1) && reviewData?.financialSnapshotUI;

  const [modalVisible, setModalVisible] = useState(false)
  const [allVisible, setAllVisible] = useState(false)
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null)
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false)

  const { questionOfDay, handleAnswerPress } = useQuestionOfDay({
    selectedAnswer,
    isAnswerRevealed,
    setSelectedAnswer,
    setIsAnswerRevealed,
    authedRequestFn,
    queryClient,
  })

  const isMobile = width < 800 || Platform.OS !== 'web';

  const modalPressHandler = () => {
    if (isMobile) {
      if (Platform.OS === 'web') {
        window.scrollTo(0,0);
      }
      navigate('/savvy');
    } else {
      setModalVisible(!modalVisible);
    }
  }

  const keyInsightsReadyToShow = !isLoading && Array.isArray(reviewData?.insights) && reviewData.insights.length > 0;

  const mainContent = dataIsLoading
  ? <Spinner size="large" color={TZABrandGuidelines.colorsMap.primaryGreen} /> 
  : financialProfileCompleted
    ? <>
      <YStack p="$3" {...dimensionProps} style={{alignItems: 'center'}}>
        <FlatListGridView
          data={allVisible || !isMobile ? dataHome : dataHome?.slice(0, 6)}
          iconContainer={{ height: 80, width: 80 }}
          container={{ /* marginBottom: 40, */ paddingTop: 20, paddingBottom: 20 }}
          parentWidth={dimensions.width}
          //columns={!isMobile ? (width <= 1000 ? 4 : 6) : undefined}
        />
        {isMobile && (
          <ActiveButton
            onPress={() => setAllVisible(!allVisible)}
            ButtonTextProps={{children: allVisible ? 'Show less' : 'Show more'}}
            backgroundColor={color.tzaGreen}
            style={{width: 120}}
            //height={47}
          />
        )}
      </YStack>
      <QuestionOfDaySection
        questionOfDay={questionOfDay}
        handleAnswerPress={handleAnswerPress}
        selectedAnswer={selectedAnswer}
        isAnswerRevealed={isAnswerRevealed}
      />
      {keyInsightsReadyToShow ? (
        <>
          <XStack justifyContent={'space-between'} p="$4">
            <SimpleText fontWeight="700" fontSize={18} color={color.darkNavy} marginVertical="$2">
              Key Insights
            </SimpleText>
            {/* <ActionButton
              bg={color.tzaGreen}
              borderRadius={4}
              fontSize={12}
              fontWeight={'600'}
              color={color.white}
              height={32}
              width={102}
            >
              Customize
            </ActionButton> */}
          </XStack>
          <KeyInsights insights={reviewData.insights} />
          {/* Array.isArray(reviewData?.insights) && reviewData.insights.length > 0 ? (
            <KeyInsights insights={reviewData.insights} />
          ) : (
            <SimpleText paddingHorizontal={16} color={color.grey}>
              No Insights to Show!
            </SimpleText>
          ) */}
        </>
      ) : null /* (
        <Spinner color={color.tzaGreen} mt={20} size="small" />
      ) */}
    </>
    : <YStack p="$4">
    <FinancialProfileButton buttonText='Click here to get started' />
  </YStack>

  return (
    <>
      <YStack style={{ display: userDataQuery.isLoading ? 'block' : 'none', backgroundColor: 'white', position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1 }}>
        <DataLoadingDialog open={userDataQuery.isLoading} onOpenChange={() => void 0} />
      </YStack>
      <ScrollView
        //flex={1}
        bg={color.white}
        nestedScrollEnabled
        scrollEnabled={!modalVisible}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 30 }}
      >
        <HomeMainHeading type="Home" image={assets.home} welcome={
          <WelcomeSection user={user} profileFullname={financialProfileQuery?.data?.["Full name"]} />
        } />
        {mainContent}
      </ScrollView>
      {financialProfileCompleted ? <>
        <ActionButton
          $platform-native={{ position: 'absolute' }}
          $platform-web={{ position: 'fixed' }}
          bottom={20}
          right={20}
          backgroundColor="#4CAF50"
          height={50}
          width={50}
          borderRadius={25}
          boxShadow={'0px 0px 6px rgba(255, 255, 255, .6)'}
          alignItems="center"
          justifyContent="center"
          onPress={modalPressHandler}
        >
          <Image 
            src={assets.SavvyChatOpen} 
            style={{height: 50, width: 50, /* ...getImageColorStyle('white') */}}
            resizeMode="contain" 
          />
        </ActionButton>
        {modalVisible && (
          <ModalDialog
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
          />
        )}
      </> : null}
    </>
  )
}
